/*
 * AddCompanyModal
 * Lets a user signing up create their company when it is not in the list.
 * Collects the same basic details as the back-office add-brand form.
 */

import * as React from 'react';
import { Button, Col, Modal } from 'react-bootstrap';
import {
    AbstractControl,
    FieldConfig,
    FormGenerator,
    FormGroup,
    Validators
} from 'react-reactive-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { msalFetch } from './Auth-Utils';
import API from '../../constants/apiEndpoints';
import { constants } from '../../constants/constants';
import { FormUtil } from '../common/FormUtil';
import { Ibrand } from '../../models';
import { replaceEmptyStringsWithNull } from '../../helpers/cleanObjEmptyStrings';

interface Iprops {
    show: boolean;
    onHide: () => void;
    onCompanyCreated: (company: Ibrand) => void;
}

interface Istate {
    loading: boolean;
}

const buildFieldConfig = (): FieldConfig => ({
    controls: {
        name: {
            options: {
                validators: [FormUtil.validators.requiredWithTrim]
            },
            render: FormUtil.TextInput,
            meta: {
                label: 'companyName',
                colWidth: 12,
                type: 'text',
                name: 'company-name'
            }
        },
        url: {
            render: FormUtil.TextInput,
            meta: {
                label: 'website',
                colWidth: 12,
                type: 'text',
                name: 'company-url'
            }
        },
        phoneNumber: {
            options: {
                validators: [Validators.maxLength(50)]
            },
            render: FormUtil.TextInput,
            meta: {
                label: 'phone',
                colWidth: 12,
                type: 'tel',
                name: 'company-phone'
            }
        },
        address: {
            render: FormUtil.TextInput,
            meta: {
                label: 'address',
                colWidth: 12,
                colWidthMedium: 6,
                type: 'text',
                name: 'company-address'
            }
        },
        address2: {
            render: FormUtil.TextInput,
            meta: {
                label: 'address2',
                colWidth: 12,
                colWidthMedium: 6,
                type: 'text',
                name: 'company-address2'
            }
        },
        city: {
            render: FormUtil.TextInput,
            meta: {
                label: 'city',
                colWidth: 12,
                colWidthMedium: 5,
                type: 'text',
                name: 'company-city'
            }
        },
        state: {
            render: FormUtil.TextInput,
            meta: {
                label: 'manageBrand:manageBrandForm.state',
                colWidth: 12,
                colWidthMedium: 3,
                type: 'text',
                name: 'company-state'
            }
        },
        postalCode: {
            options: {
                validators: [
                    Validators.pattern(
                        /^[a-zA-Z0-9][a-zA-Z0-9\- ]{0,10}[a-zA-Z0-9]$/
                    )
                ]
            },
            render: FormUtil.TextInput,
            meta: {
                label: 'zip',
                colWidth: 12,
                colWidthMedium: 4,
                type: 'text',
                name: 'company-postal-code'
            }
        },
        countryID: {
            render: FormUtil.Select,
            meta: {
                options: constants.countries,
                label: 'country',
                colWidth: 12,
                placeholder: 'manageUserQueue:countrySearchPlaceholder',
                name: 'company-country',
                isClearable: true,
                // open upward when there is no room below instead of scrolling
                // the modal body (which visibly shifted the whole form up)
                menuPlacement: 'auto',
                maxMenuHeight: 220
            }
        }
    }
});

class AddCompanyModal extends React.Component<
    Iprops & WithTranslation,
    Istate
> {
    public form: FormGroup | any;
    public fieldConfig: FieldConfig;

    constructor(props: Iprops & WithTranslation) {
        super(props);
        this.state = {
            loading: false
        };
        this.fieldConfig = FormUtil.translateForm(
            buildFieldConfig(),
            this.props.t
        );
    }

    setForm = (form: AbstractControl) => {
        this.form = form;
    };

    handleSubmit = (evt: React.MouseEvent<HTMLFormElement>) => {
        evt.preventDefault();
        if (this.form.status === 'INVALID') {
            this.form.markAsSubmitted();
            toastr.error(
                this.props.t('validationError'),
                '',
                constants.toastrError
            );
            return;
        }

        this.setState({ loading: true });

        // empty strings become null so nullable value types (Guid? countryID) bind cleanly
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data: replaceEmptyStringsWithNull(
                FormUtil.getValues(this.form.value)
            )
        };

        msalFetch(API.POST.brandSignupCreate, axiosOptions)
            .then((response: AxiosResponse<any>) => {
                this.setState({ loading: false });
                if (!response.data) {
                    throw new Error('missing data');
                }
                this.props.onCompanyCreated(response.data as Ibrand);
            })
            .catch((error: any) => {
                this.setState({ loading: false });
                constants.handleError(error, 'create company');
                console.error(error);
            });
    };

    render() {
        const { t } = this.props;

        return (
            <Modal
                show={this.props.show}
                onHide={this.props.onHide}
                className="add-company-modal"
            >
                <Modal.Header>
                    <Modal.Title>{t('addCompanyTitle')}</Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={this.props.onHide}
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <form
                        onSubmit={this.handleSubmit}
                        className="clearfix beacon-form"
                    >
                        <FormGenerator
                            onMount={this.setForm}
                            fieldConfig={this.fieldConfig}
                        />
                        <Col xs={12} className="form-buttons text-right">
                            <Button
                                bsStyle="default"
                                type="button"
                                className="cancel-button"
                                disabled={this.state.loading}
                                onClick={this.props.onHide}
                            >
                                {t('common:cancel')}
                            </Button>
                            <Button
                                bsStyle="primary"
                                type="submit"
                                disabled={this.state.loading}
                            >
                                {t('common:save')}
                            </Button>
                        </Col>
                    </form>
                </Modal.Body>
            </Modal>
        );
    }
}

export default withTranslation('user')(AddCompanyModal);
